import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { 
  FaShoppingCart, 
  FaCalculator, 
  FaIndustry, 
  FaHeartbeat, 
  FaTruck, 
  FaLaptopCode 
} from 'react-icons/fa';
import 'bootstrap/dist/css/bootstrap.min.css';

const industries = [
  {
    title: "Retail & E-commerce",
    description: "We provide expert NetSuite solutions for Retail & E-commerce, helping businesses streamline operations, manage inventory, enhance customer experiences, and drive growth.",
    icon: <FaShoppingCart />,
    color: "#FF6B6B",
    gradient: "linear-gradient(135deg, #FF6B6B, #FF8E53)",
    stats: "500+ stores",
    path: "/netsuite/industries/retail-ecommerce"
  },
  {
    title: "Finance & Accounting",
    description: "Simplify finance and accounting with our NetSuite solutions. Automate processes, ensure compliance, and gain real-time financial insights for smarter decision-making.",
    icon: <FaCalculator />,
    color: "#4ECDC4",
    gradient: "linear-gradient(135deg, #4ECDC4, #44A08D)",
    stats: "99% accuracy",
    path: "/netsuite/industries/finance-accounting"
  },
  {
    title: "Manufacturing",
    description: "Optimize your manufacturing operations with our NetSuite solutions. Streamline production, inventory, and supply chain management for maximum efficiency and growth.",
    icon: <FaIndustry />,
    color: "#45B7D1",
    gradient: "linear-gradient(135deg, #45B7D1, #2196F3)",
    stats: "40% efficiency",
    path: "/netsuite/industries/manufacturing"
  },
  {
    title: "Healthcare & Services",
    description: "Optimize healthcare and service operations with our NetSuite solutions. Streamline billing, compliance, and patient management for improved efficiency and care.",
    icon: <FaHeartbeat />,
    color: "#A78BFA",
    gradient: "linear-gradient(135deg, #A78BFA, #8B5CF6)",
    stats: "24/7 support",
    path: "/netsuite/industries/healthcare"
  },
  {
    title: "Wholesale & Distribution",
    description: "Enhance your wholesale and distribution operations with our NetSuite solutions. Streamline inventory, orders, and supply chain for greater efficiency and profitability.",
    icon: <FaTruck />,
    color: "#F9A826",
    gradient: "linear-gradient(135deg, #F9A826, #FF8C00)",
    stats: "95% on-time",
    path: "/netsuite/industries/wholesale-distribution"
  },
  {
    title: "IT & Software",
    description: "Power your IT and software business with our NetSuite solutions. Streamline operations, automate billing, and scale effortlessly with real-time insights and Enhance decision-making ",
    icon: <FaLaptopCode />,
    color: "#6C5CE7",
    gradient: "linear-gradient(135deg, #6C5CE7, #5A67D8)",
    stats: "100+ apps",
    path: "/netsuite/industries/it-software"
  }
];

const IndustryCard = ({ industry, index }) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  React.useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.8, rotateY: -15 },
    visible: {
      opacity: 1,
      scale: 1,
      rotateY: 0,
      transition: {
        delay: index * 0.2,
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={cardVariants}
      className="industry-card position-relative"
    >
      {/* Background gradient card */}
      <div 
        className="card-background"
        style={{
          background: industry.gradient,
          borderRadius: '24px',
          padding: '1px',
          height: '100%'
        }}
      >
        <div 
          className="card-inner bg-white"
          style={{
            borderRadius: '23px',
            height: '100%',
            padding: '2rem',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* Decorative circles */}
          <div
            style={{
              position: 'absolute',
              top: '-20px',
              right: '-20px',
              width: '80px',
              height: '80px',
              background: industry.gradient,
              borderRadius: '50%',
              opacity: 0.1
            }}
          />
          <div
            style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              background: industry.gradient,
              borderRadius: '50%',
              opacity: 0.05
            }}
          />

          {/* Content */}
          <div className="position-relative" style={{ zIndex: 2 }}>
            {/* Header with icon and stats */}
            <div className="d-flex justify-content-between align-items-start mb-3">
              <div 
                className="icon-container d-flex align-items-center justify-content-center"
                style={{
                  width: '70px',
                  height: '70px',
                  background: industry.gradient,
                  borderRadius: '20px',
                  color: 'white',
                  fontSize: '1.8rem',
                  boxShadow: `0 10px 30px ${industry.color}40`
                }}
              >
                {industry.icon}
              </div>
              {/* <div className="text-end">
                <div 
                  className="stats-badge px-3 py-1 rounded-pill fw-bold"
                  style={{
                    background: `${industry.color}20`,
                    color: industry.color,
                    fontSize: '0.85rem',
                    border: `2px solid ${industry.color}30`
                  }}
                >
                  {industry.stats}
                </div>
              </div> */}
            </div>

            {/* Title */}
            <h4 
              className="fw-bold mb-3"
              style={{
                background: industry.gradient,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              {industry.title}
            </h4>

            {/* Description */}
            <p className="text-muted mb-4" style={{ fontSize: '0.95rem', lineHeight: '1.6' }}>
              {industry.description}
            </p>

            {/* Action button */}
            <div className="d-flex align-items-center">
              <Link to={industry.path} className="text-decoration-none">
                <div
                  className="btn fw-semibold px-4 py-2 border-0 rounded-pill"
                  style={{
                    background: industry.gradient,
                    color: 'white',
                    fontSize: '0.9rem',
                    boxShadow: `0 8px 25px ${industry.color}40`,
                    display: 'inline-block'
                  }}
                >
                  Explore Solutions
                </div>
              </Link>
              <div
                className="ms-3"
                style={{
                  color: industry.color,
                  fontSize: '1.2rem'
                }}
              >
                →
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const Industries = () => {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  React.useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  const textVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  return (
    <section className="industries-section position-relative overflow-hidden" style={{
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      minHeight: '100vh',
      paddingTop: '5rem',
      paddingBottom: '5rem'
    }}>
      {/* Animated background elements */}
      <div className="position-absolute w-100 h-100" style={{ top: 0, left: 0, zIndex: 1 }}>
        <div
          style={{
            position: 'absolute',
            top: '10%',
            left: '5%',
            width: '200px',
            height: '200px',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '50%',
            animation: 'float 6s ease-in-out infinite'
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: '60%',
            right: '10%',
            width: '150px',
            height: '150px',
            background: 'rgba(255, 255, 255, 0.08)',
            borderRadius: '50%',
            animation: 'float 4s ease-in-out infinite reverse'
          }}
        />
        <div
          style={{
            position: 'absolute',
            bottom: '20%',
            left: '15%',
            width: '100px',
            height: '100px',
            background: 'rgba(255, 255, 255, 0.06)',
            borderRadius: '50%',
            animation: 'float 5s ease-in-out infinite'
          }}
        />
      </div>

      <Container style={{ position: 'relative', zIndex: 2 }}>
        {/* Header Section */}
        <motion.div 
          className="text-center text-white mb-5 pb-4"
          initial="hidden"
          animate={controls}
          variants={textVariants}
          ref={ref}
        >
          <div className="mb-4">
            <span 
              className="badge px-4 py-2 fw-bold fs-6 mb-3"
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                borderRadius: '50px',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.3)'
              }}
            >
              ✨ INDUSTRIES WE TRANSFORM
            </span>
          </div>
          <h1 className="fw-bold mb-4" style={{ fontSize: '3.5rem', lineHeight: '1.2' }}>
            Digitizing Businesses<br />
            <span style={{ 
              background: 'linear-gradient(45deg, #FFD700, #FFA500)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Across Industries
            </span>
          </h1>
          <p className="lead mx-auto opacity-75" style={{ maxWidth: '600px', fontSize: '1.3rem' }}>
            From startups to Fortune 500 companies, we deliver industry-specific NetSuite solutions that drive 
            <span className="fw-bold text-warning"> measurable results</span>
          </p>
        </motion.div>

        {/* Cards Grid */}
        <Row className="g-4 mt-5">
          {industries.map((industry, index) => (
            <Col lg={4} md={6} key={index}>
              <IndustryCard industry={industry} index={index} />
            </Col>
          ))}
        </Row>

        {/* Bottom CTA */}
        {/* <motion.div 
          className="text-center mt-5 pt-5"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.6 }}
        >
          <motion.button
            className="btn btn-light btn-lg px-5 py-3 fw-semibold rounded-pill"
            whileHover={{ 
              scale: 1.05,
              boxShadow: "0 15px 40px rgba(255, 255, 255, 0.3)"
            }}
            whileTap={{ scale: 0.95 }}
            style={{
              fontSize: '1.1rem',
              boxShadow: '0 10px 30px rgba(255, 255, 255, 0.2)'
            }}
          >
            Discover Your Industry Solution →
          </motion.button>
        </motion.div> */}
      </Container>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
      `}</style>
    </section>
  );
};

export default Industries;